/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 担当割当の担当カレンダー。サービスと月を選び、ロスター変更履歴（OpsAssignmentEvent[]）から opsDutyMonth で各日の当番（オペ担 / review_sql）を導出して月グリッド（日曜始まり）で表示する。カバー（当日代理 / 休日オペたん）由来の日は 🎌 + warning 配色で強調し通常ローテーションと区別する。担当のいない日は muted の「—」。過ぎて掃除されたカバーも当時のイベントに残るため正しく再現される
 * @graph-connects opsDutyMonth [calls] 履歴スナップショットから指定月の各日の当番を導出
 * @graph-connects jstDateKey [calls] 今日の JST 年月を既定表示月にするための整形
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import {
  OPS_SERVICE_LABELS,
  OPS_SERVICE_SLUGS,
  jstDateKey,
  opsDutyMonth,
  type OpsDutyDay,
  type OpsService,
} from '@cortex/ops-core';
import type { OpsAssignmentEvent } from '../../../lib/api/ops.js';

/** 曜日ヘッダ（日曜始まり。i18n キー `ops.assignments.calendar.weekdays.<key>` を解決する）。 @graph-connects none */
const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/**
 * 指定年月（1-based）の 1 日の曜日（0=日〜6=土）。暦日の曜日はプロセス TZ に依存しないため UTC で算出する
 * （先頭の空セル数 = 1 日の曜日）。
 * @graph-connects none
 */
export function firstWeekdayOf(year: number, month: number): number {
  return new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
}

/** 表示月を delta（±1）ずらす。1↔12 月の境界で年を繰り上げ / 繰り下げる。 @graph-connects none */
export function stepMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

/** 既定表示サービス（OPS_SERVICE_SLUGS の先頭。空配列時のみ 'aircloset' へフォールバック）。 @graph-connects none */
export function defaultCalendarService(slugs: OpsService[]): OpsService {
  return slugs[0] ?? 'aircloset';
}

/** 当番日配列の前後に空セル（null）を詰めて 7 列グリッドに揃える（先頭=1 日の曜日、末尾=週の残り）。 @graph-connects none */
export function padDutyGrid(days: OpsDutyDay[], leading: number): (OpsDutyDay | null)[] {
  const cells: (OpsDutyDay | null)[] = [
    ...Array.from<null>({ length: leading }).fill(null),
    ...days,
  ];
  const trailing = (7 - (cells.length % 7)) % 7;
  return [...cells, ...Array.from<null>({ length: trailing }).fill(null)];
}

/** DutyRoleLine の props。 */
interface DutyRoleLineProps {
  /** 役割ラベル（オペ担 / review_sql）。 */
  label: string;
  /** その日の担当（null = 該当なし）。 */
  who: string | null;
  /** カバー由来か（true なら 🎌 + warning 配色で強調）。 */
  fromCover: boolean;
}

/** 1 役割ぶんの当番表示。担当なしは muted の「—」、カバー由来は 🎌 + warning で強調する。 @graph-connects none */
function DutyRoleLine({ label, who, fromCover }: DutyRoleLineProps): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 leading-tight">
      <span className="text-[10px] text-[var(--color-text-muted)]">{label}</span>
      {who === null ? (
        <span className="text-[var(--color-text-muted)]">—</span>
      ) : fromCover ? (
        <span className="inline-flex items-center gap-0.5 rounded bg-[var(--color-warning)] px-1 font-medium text-white">
          <span aria-label={t('ops.assignments.calendar.coverFlag')}>🎌</span>
          {who}
        </span>
      ) : (
        <span className="text-[var(--color-text-primary)]">{who}</span>
      )}
    </div>
  );
}

/** DutyCell の props。 */
interface DutyCellProps {
  /** 1 日ぶんの当番実績。 */
  day: OpsDutyDay;
}

/** カレンダー 1 日分のセル（日番号 + オペ担 + review_sql 当番）。カバー由来は強調表示する。 @graph-connects none */
function DutyCell({ day }: DutyCellProps): ReactNode {
  const { t } = useTranslation();
  const dayNumber = Number(day.date.slice(8, 10));
  return (
    <div
      role="gridcell"
      aria-label={day.date}
      className="flex min-h-[4.75rem] flex-col gap-0.5 rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-1 text-xs"
    >
      <div className="text-right text-[var(--color-text-muted)]">{dayNumber}</div>
      <DutyRoleLine
        label={t('ops.assignments.calendar.opetanRole')}
        who={day.opetan}
        fromCover={day.opetanFromCover}
      />
      <DutyRoleLine
        label={t('ops.assignments.calendar.reviewerRole')}
        who={day.reviewer}
        fromCover={day.reviewerFromCover}
      />
    </div>
  );
}

/** OpsDutyCalendar の props。 */
export interface OpsDutyCalendarProps {
  /**
   * 全サービスのロスター変更履歴。新しい順でなくてよい（opsDutyMonth が effectiveFrom で対象日のロスターを
   * 解決する）。サービス選択で `service` によりフィルタしてから当番を導出する。
   */
  events: OpsAssignmentEvent[];
}

/**
 * サービス別・月別に「誰がその日オペ担 / review_sql 当番だったか」を月グリッド（日曜始まり）で表示する
 * カレンダー。ロスター変更履歴から opsDutyMonth で各日の当番を導出し、カバー由来の日は 🎌 + warning で
 * 強調する。既定はサービス先頭 slug と今日（JST）の年月。狭幅では横スクロールし、ページ本体は横スクロール
 * させない。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 担当割当の担当カレンダー。サービス・月を選び、履歴から各日の当番（オペ担 / review_sql）を再現して月グリッド表示し、カバー由来日を強調する
 * @graph-connects opsDutyMonth [calls] 履歴スナップショットから指定月の各日の当番を導出
 * @graph-connects jstDateKey [calls] 既定表示月（今日の JST 年月）の整形
 */
export function OpsDutyCalendar({ events }: OpsDutyCalendarProps): ReactNode {
  const { t } = useTranslation();
  const [service, setService] = useState<OpsService>(() =>
    defaultCalendarService(OPS_SERVICE_SLUGS),
  );
  const [ym, setYm] = useState<{ year: number; month: number }>(() => {
    const key = jstDateKey(new Date());
    return { year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)) };
  });

  const serviceOptions: SelectOption[] = useMemo(
    () => OPS_SERVICE_SLUGS.map((s) => ({ value: s, label: OPS_SERVICE_LABELS[s] })),
    [],
  );

  const cells = useMemo<(OpsDutyDay | null)[]>(() => {
    const snapshots = events.filter((e) => e.service === service);
    const days = opsDutyMonth(snapshots, ym.year, ym.month);
    return padDutyGrid(days, firstWeekdayOf(ym.year, ym.month));
  }, [events, service, ym]);

  return (
    <Card padding="md">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Select
          options={serviceOptions}
          value={service}
          aria-label={t('ops.assignments.calendar.serviceLabel')}
          onChange={(e) => {
            setService(e.target.value as OpsService);
          }}
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('ops.assignments.calendar.prevMonth')}
            onClick={() => {
              setYm((prev) => stepMonth(prev.year, prev.month, -1));
            }}
          >
            ‹
          </Button>
          <span
            className="min-w-[7rem] text-center text-sm font-medium text-[var(--color-text-primary)]"
            aria-live="polite"
          >
            {t('ops.assignments.calendar.monthLabel', { year: ym.year, month: ym.month })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('ops.assignments.calendar.nextMonth')}
            onClick={() => {
              setYm((prev) => stepMonth(prev.year, prev.month, 1));
            }}
          >
            ›
          </Button>
        </div>
      </div>

      <p className="mt-2 text-xs text-[var(--color-text-muted)]">
        {t('ops.assignments.calendar.intro')}
      </p>
      <p className="mt-1 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
        <span
          aria-hidden="true"
          className="inline-flex items-center rounded bg-[var(--color-warning)] px-1 font-medium text-white"
        >
          🎌
        </span>
        <span>{t('ops.assignments.calendar.legend')}</span>
      </p>

      <div className="mt-3 overflow-x-auto">
        <div role="grid" className="min-w-[640px]">
          <div role="row" className="grid grid-cols-7 gap-1">
            {WEEKDAY_KEYS.map((key) => (
              <div
                key={key}
                role="columnheader"
                className="px-1 py-1 text-center text-xs font-medium text-[var(--color-text-muted)]"
              >
                {t(`ops.assignments.calendar.weekdays.${key}`)}
              </div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {cells.map((cell, index) =>
              cell === null ? (
                <div
                  key={`blank-${String(index)}`}
                  aria-hidden="true"
                  className="min-h-[4.75rem]"
                />
              ) : (
                <DutyCell key={cell.date} day={cell} />
              ),
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
